import React, { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../lib/lang";
import { useSilentMode } from "../lib/useSilentMode";

const HOLD_MS = 1000;
const SIZE = 340;       // button outer diameter — dominant focal point
const PAD = 36;         // outer breathing room for flashing rings (px each side)
const FRAME = SIZE + PAD * 2;
const RING_R = 168;     // progress ring radius (px, from center)
const STROKE = 8;       // green countdown stroke — thicker so it reads from the back row
const CIRC = 2 * Math.PI * RING_R;

// Countdown ring color — emerald green, deliberately NOT red so the
// hold progress is unmistakable against the deep crimson dome and
// communicates "go / progressing toward activation".
const COUNTDOWN_COLOR = "#10B981"; // tailwind emerald-500
const COUNTDOWN_GLOW = "rgba(16,185,129,0.50)";

// Subtle haptic pulse on hold completion — communicates the moment
// of activation through touch as well as sight. ``navigator.vibrate``
// is a no-op on iOS Safari but works on most Android browsers + many
// PWAs; gracefully ignored where unsupported.
const fireHaptic = () => {
  try { navigator.vibrate?.(35); } catch (_e) { /* noop */ }
};

/**
 * Physical-button SosButton.
 *
 * Two interaction modes:
 *   • `"hold"`  (default) — press-and-hold 2.5 s. On completion fires
 *      `onConfirm()`. This is the legacy gateway into the alert flow.
 *   • `"instant"` — single tap. Fires `onConfirm()` on touchstart /
 *      mousedown. The SOS button never sends an alert in this mode —
 *      it only opens the type picker. Final dispatch happens on the
 *      review screen.
 *   • `"armed"` — legacy single-tap "final send" mode. Kept for
 *      backwards compatibility but no longer used by the home flow.
 */
export const SosButton = ({ onConfirm, disabled, mode = "hold", armedLabel }) => {
  const [t] = useT();
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const startRef = useRef(0);
  const rafRef = useRef(0);
  const [silent] = useSilentMode();

  const isArmed = mode === "armed";
  const isInstant = mode === "instant";

  const stop = () => {
    if (isArmed || isInstant) return;
    setHolding(false);
    cancelAnimationFrame(rafRef.current);
    setProgress(0);
  };

  const tickRef = useRef(() => {});
  const tick = useCallback(() => {
    const elapsed = Date.now() - startRef.current;
    const p = Math.min(1, elapsed / HOLD_MS);
    setProgress(p);
    if (p >= 1) {
      cancelAnimationFrame(rafRef.current);
      setHolding(false);
      fireHaptic();
      onConfirm?.();
      setProgress(0);
      return;
    }
    rafRef.current = requestAnimationFrame(tickRef.current);
  }, [onConfirm]);
  // Keep the ref pointed at the latest tick closure so the rAF chain
  // re-reads through the ref (avoids TDZ self-reference at lint time).
  useEffect(() => { tickRef.current = tick; }, [tick]);

  const start = (e) => {
    if (disabled) return;
    e.preventDefault?.();
    if (isInstant || isArmed) {
      // Single-tap modes — fire on touchstart / mousedown.
      onConfirm?.();
      return;
    }
    startRef.current = Date.now();
    setHolding(true);
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const dashOffset = CIRC * (1 - progress);
  const showHoldEmit = !isArmed && !isInstant && !disabled && !silent && !holding;
  const showArmedEmit = isArmed && !disabled && !silent;

  return (
    <div className="flex flex-col items-center gap-4 select-none" data-testid="sos-button-wrapper">
      <div className="relative" style={{ width: FRAME, height: FRAME }}>

        {/* ── Layer 1: flashing emit rings ─────────────────────────────
            Two staggered slow rings + a softer static halo. They live
            *outside* the button itself so the press doesn't compress
            them. Hidden during silent mode and during a hold to keep
            attention on the green countdown. */}
        {showHoldEmit && (
          <>
            <span
              aria-hidden
              className="absolute rounded-full border-2 border-[#22D3EE]/45 animate-sos-emit"
              style={{
                width: SIZE,
                height: SIZE,
                left: PAD,
                top: PAD,
                willChange: "transform, opacity",
              }}
            />
            <span
              aria-hidden
              className="absolute rounded-full border-2 border-[#22D3EE]/32 animate-sos-emit"
              style={{
                width: SIZE,
                height: SIZE,
                left: PAD,
                top: PAD,
                animationDelay: "1.4s",
                willChange: "transform, opacity",
              }}
            />
            {/* Static soft halo behind the button — gives the dome lift
                without competing with the emit pulses. */}
            <span
              aria-hidden
              className="absolute rounded-full pointer-events-none"
              style={{
                width: SIZE + 24,
                height: SIZE + 24,
                left: PAD - 12,
                top: PAD - 12,
                background: "radial-gradient(circle, rgba(127,29,29,0.18) 0%, rgba(127,29,29,0) 70%)",
              }}
            />
          </>
        )}
        {/* ── Armed mode emit: faster, amber-tinted, more aggressive ─── */}
        {showArmedEmit && (
          <>
            <span
              aria-hidden
              className="absolute rounded-full border-[3px] border-amber-400/65 animate-armed-emit"
              style={{ width: SIZE, height: SIZE, left: PAD, top: PAD, willChange: "transform, opacity" }}
            />
            <span
              aria-hidden
              className="absolute rounded-full border-[3px] border-amber-300/55 animate-armed-emit"
              style={{ width: SIZE, height: SIZE, left: PAD, top: PAD, animationDelay: "0.6s", willChange: "transform, opacity" }}
            />
            <span
              aria-hidden
              className="absolute rounded-full border-[3px] border-amber-200/45 animate-armed-emit"
              style={{ width: SIZE, height: SIZE, left: PAD, top: PAD, animationDelay: "1.2s", willChange: "transform, opacity" }}
            />
          </>
        )}

        {/* ── Layer 2 + 3: SVG ring stack (background track + amber countdown) ── */}
        <svg
          aria-hidden
          className="absolute inset-0 -rotate-90 pointer-events-none"
          width={FRAME}
          height={FRAME}
          viewBox={`0 0 ${FRAME} ${FRAME}`}
        >
          {/* Faint track so the ring's path is always visible at rest. */}
          <circle
            cx={FRAME / 2}
            cy={FRAME / 2}
            r={RING_R}
            stroke="rgba(10,17,40,0.10)"
            strokeWidth={STROKE}
            fill="none"
          />
          {/* GREEN countdown ring — only paints while holding. Uses a
              dasharray-offset technique for smooth, precise progress. */}
          <circle
            cx={FRAME / 2}
            cy={FRAME / 2}
            r={RING_R}
            stroke={COUNTDOWN_COLOR}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={dashOffset}
            fill="none"
            style={{
              filter: holding ? `drop-shadow(0 0 8px ${COUNTDOWN_GLOW})` : "none",
              opacity: holding ? 1 : 0,
              transition: holding
                ? "stroke-dashoffset 0.05s linear, opacity 120ms ease-out"
                : "stroke-dashoffset 0.3s ease-out, opacity 200ms ease-out",
            }}
          />
        </svg>

        {/* ── Layer 4: the physical button dome ─────────────────────── */}
        <button
          type="button"
          aria-label={t.pressAndHold}
          data-testid="sos-main-button"
          disabled={disabled}
          onMouseDown={start}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchEnd={stop}
          onTouchCancel={stop}
          style={{
            width: SIZE,
            height: SIZE,
            left: PAD,
            top: PAD,
            // Stacked gradients give the dome real depth:
            //   • A bright top-left specular highlight (24% white)
            //   • A radial body gradient from crimson → maroon → near-black
            //     centered slightly off-axis so the dome reads 3-D
            //   • A subtle linear gradient over the top for a rim-light pass
            backgroundImage: [
              "radial-gradient(ellipse 60% 55% at 32% 22%, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0) 55%)",
              "radial-gradient(circle at 50% 60%, #B91C1C 0%, #7F1D1D 55%, #4B0810 100%)",
              "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.10) 100%)",
            ].join(", "),
            // Multi-layer shadow stack — outer drop for grounding,
            // colored ambient halo for emergency identity,
            // inset top highlight + inset bottom shadow for bevel.
            boxShadow: holding
              ? [
                  "0 6px 18px rgba(0,0,0,0.35)",
                  "0 14px 32px -10px rgba(127,29,29,0.40)",
                  "inset 0 2px 1px rgba(255,255,255,0.20)",
                  "inset 0 -14px 28px rgba(0,0,0,0.40)",
                  "inset 0 6px 14px rgba(0,0,0,0.18)", // pressed look
                ].join(", ")
              : [
                  "0 12px 28px rgba(0,0,0,0.30)",
                  "0 24px 60px -16px rgba(127,29,29,0.35)",
                  "inset 0 3px 2px rgba(255,255,255,0.25)",
                  "inset 0 -16px 34px rgba(0,0,0,0.38)",
                  "inset 0 0 0 1px rgba(255,255,255,0.05)", // crisp edge highlight
                ].join(", "),
            transform: holding ? "translateY(2px)" : "translateY(0)",
          }}
          className={`absolute rounded-full text-white flex flex-col items-center justify-center
            transition-[transform,box-shadow] duration-150 ease-out
            ${holding ? "scale-[0.97]" : "active:scale-[0.98]"}
            ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
            focus:outline-none focus-visible:ring-4 focus-visible:ring-[#7F1D1D]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background
          `}
        >
          {/* Inner concentric ring etched into the dome — adds bezel feel. */}
          <span
            aria-hidden
            className="pointer-events-none absolute rounded-full"
            style={{
              inset: 12,
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10), inset 0 0 18px rgba(0,0,0,0.25)",
            }}
          />

          {silent && (
            <span
              className="absolute top-6 left-1/2 -translate-x-1/2 text-[9px] font-bold tracking-[0.3em] uppercase bg-white text-mali-navy px-2 py-0.5 rounded-full"
              data-testid="silent-badge"
            >
              {t.silentBadge}
            </span>
          )}
          <span
            className="font-display text-[110px] font-black tracking-[-0.06em] leading-none relative z-10"
            style={{ textShadow: "0 2px 0 rgba(0,0,0,0.35), 0 4px 18px rgba(0,0,0,0.30)" }}
          >
            SOS
          </span>
          <span
            className="mt-3 text-[12px] font-bold tracking-[0.32em] uppercase text-white/95 relative z-10 px-6 text-center leading-tight"
            style={{ textShadow: "0 1px 0 rgba(0,0,0,0.35)" }}
          >
            {isInstant
              ? (t.sosTapToStart || "APPUYEZ POUR DÉMARRER")
              : isArmed
                ? (armedLabel || t.tapToSendNow)
                : (holding ? t.hold : t.holdCta)}
          </span>
        </button>
      </div>

      <p
        className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground text-center px-6"
        data-testid="sos-instruction"
      >
        {isInstant
          ? (t.sosTapHelper || "Appuyez pour démarrer une nouvelle alerte")
          : isArmed
            ? (t.tapToSendInstruction || t.tapToSendNow)
            : t.pressAndHold}
      </p>
    </div>
  );
};
